import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '@components/Icon';
import SEO from '@components/seo/SEO';
import LanguageSwitcher from '@components/LanguageSwitcher';
import { BackTop, Card, Divider, Col, Row, Drawer } from 'antd';
import { isRtlLanguage } from '@i18n/config';

// 创建图标封装组件，与LandingPage中相同
const SafeIcon = ({ icon: IconName, useFa = true, ...props }) => {
  if (typeof IconName === 'string' && Icon[IconName]) {
    const IconComponent = Icon[IconName];
    return <IconComponent useFa={useFa} {...props} />;
  }
  
  if (typeof IconName === 'function' || typeof IconName === 'object') {
    return <IconName useFa={useFa} {...props} />;
  }
  
  return null;
};

const About = () => {
  const { t, i18n } = useTranslation('pages');
  const { t: tCommon } = useTranslation('common');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isRtl = isRtlLanguage(i18n.language);
  
  // 设置 HTML 的 dir 属性
  useEffect(() => {
    document.documentElement.dir = isRtl ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
    return () => {
      document.documentElement.dir = 'ltr';
    };
  }, [isRtl, i18n.language]);
  
  // 其他工具数据
  const toolsList = [
    {
      key: "pdfzap",
      icon: "Zap",
      iconColor: "text-purple-500",
      url: "https://pdfzap.pixzens.com",
      color: "bg-purple-50 dark:bg-purple-900/20"
    },
    {
      key: "livp",
      icon: "Image",
      iconColor: "text-pink-500",
      url: "https://livp.toolooz.com",
      color: "bg-pink-50 dark:bg-pink-900/20"
    },
    {
      key: "twitdloader",
      icon: "FileText",
      iconColor: "text-green-500",
      url: "https://twitdloader.toolooz.com",
      color: "bg-green-50 dark:bg-green-900/20"
    },
    {
      key: "revizepic",
      icon: "ImagePlus",
      iconColor: "text-pink-500",
      url: "https://revizepic.pixzens.com",
      color: "bg-pink-50 dark:bg-pink-900/20"
    },
    {
      key: "paperme",
      icon: "FileText",
      iconColor: "text-purple-500",
      url: "https://paperme.pixzens.com",
      color: "bg-purple-50 dark:bg-purple-900/20"
    },
    {
      key: "livecapture",
      icon: "Video",
      iconColor: "text-blue-500",
      url: "https://livecapture.pixzens.com",
      color: "bg-blue-50 dark:bg-blue-900/20"
    },
    {
      key: "slimpic",
      icon: "Minimize2",
      iconColor: "text-pink-500",
      useFa: true,
      url: "https://slimpic.pixzens.com",
      color: "bg-pink-50 dark:bg-pink-900/20"
    },
    {
      key: "snaptrim",
      icon: "Scissors",
      iconColor: "text-purple-500",
      url: "https://snaptrim.pixzens.com",
      color: "bg-purple-50 dark:bg-purple-900/20"
    },
    {
      key: "picweave",
      icon: "Grid",
      iconColor: "text-purple-500",
      useFa: true,
      url: "https://picweave.pixzens.com",
      color: "bg-purple-50 dark:bg-purple-900/20"
    },
    {
      key: "cuttypic",
      icon: "Image",
      iconColor: "text-pink-500",
      url: "https://cuttypic.pixzens.com",
      color: "bg-pink-50 dark:bg-pink-900/20"
    },
    {
      key: "picsizer",
      icon: "Maximize",
      iconColor: "text-blue-500",
      url: "https://picsizer.pixzens.com",
      color: "bg-blue-50 dark:bg-blue-900/20"
    },
    {
      key: "snapcute",
      icon: "Scissors",
      iconColor: "text-amber-500",
      url: "https://snapcute.pixzens.com",
      color: "bg-amber-50 dark:bg-amber-900/20"
    },
    {
      key: "handtrim",
      icon: "Scissors",
      iconColor: "text-green-500",
      url: "https://handtrim.pixzens.com",
      color: "bg-green-50 dark:bg-green-900/20"
    }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-purple-50 to-blue-50 dark:from-gray-900 dark:to-gray-800">
      <SEO 
        title={t('about.seo.title')}
        description={t('about.seo.description')}
        keywords={t('about.seo.keywords')}
        canonicalUrl="https://snapbeautify.pixzens.com/about"
      />
      {/* 导航栏 */}
      <header className="bg-white dark:bg-gray-900 shadow-sm">
        <div className="container mx-auto px-4 py-3">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <Link to="/" className="flex items-center text-2xl font-bold text-purple-600 dark:text-purple-400">
                <img src="/favicon.svg" alt={t('about.logoText')} width="32" height="32" className="mr-2" />
                {t('about.logoText')}
              </Link>
            </div>
            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/" className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center">
                <SafeIcon icon="Home" size={16} className="mr-1" />
                {tCommon('home')}
              </Link>
              <Link to="/quick-start" className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center">
                <SafeIcon icon="BookOpen" size={16} className="mr-1" />
                {tCommon('quickStart')}
              </Link>
              <Link to="/about" className="font-medium text-purple-600 dark:text-purple-400 flex items-center">
                <SafeIcon icon="User" size={16} className="mr-1" />
                {tCommon('about')}
              </Link>
              <LanguageSwitcher />
            </nav>
            <div className="flex md:hidden">
              <button 
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                onClick={() => setMobileMenuOpen(true)}
              >
                <SafeIcon icon="Menu" size={24} />
              </button>
            </div>
          </div>
        </div>
        
        {/* 移动设备导航菜单 */}
        <Drawer
          title={tCommon('navigation')}
          placement={isRtl ? 'left' : 'right'}
          onClose={() => setMobileMenuOpen(false)}
          open={mobileMenuOpen}
          width={250}
        >
          <div className="flex flex-col space-y-4">
            <Link 
              to="/" 
              className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              <SafeIcon icon="Home" size={16} className="mr-1" />
              {tCommon('home')}
            </Link>
            <Link 
              to="/quick-start" 
              className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              <SafeIcon icon="BookOpen" size={16} className="mr-1" />
              {tCommon('quickStart')}
            </Link>
            <Link 
              to="/about" 
              className="font-medium text-purple-600 dark:text-purple-400 flex items-center py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              <SafeIcon icon="User" size={16} className="mr-1" />
              {tCommon('about')}
            </Link>
            <Link 
              to="/features" 
              className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              <SafeIcon icon="Star" size={16} className="mr-1" />
              {tCommon('features')}
            </Link>
            <Link 
              to="/faq" 
              className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              <SafeIcon icon="HelpCircle" size={16} className="mr-1" />
              {tCommon('faq')}
            </Link>
            <div className="border-t border-gray-200 dark:border-gray-700 my-2 pt-2">
              <Link 
                to="/privacy" 
                className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                <SafeIcon icon="Shield" size={16} className="mr-1" />
                {tCommon('privacy')}
              </Link>
              <Link 
                to="/terms" 
                className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                <SafeIcon icon="FileText" size={16} className="mr-1" />
                {tCommon('terms')}
              </Link>
              <Link 
                to="/copyright" 
                className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                <SafeIcon icon="Award" size={16} className="mr-1" />
                {tCommon('copyright')}
              </Link>
            </div>
          </div>
        </Drawer>
      </header>

      {/* 主要内容区域 */}
      <main className="flex-grow container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden mb-8">
            <div className="p-8">
              <h1 className="text-3xl font-bold text-gray-800 dark:text-white mb-6 flex items-center">
                <SafeIcon icon="User" className="mr-3 text-purple-600 dark:text-purple-400" size={28} useFa={true} />
                {t('about.title')}
              </h1>

              <div className="prose dark:prose-invert max-w-none">
                <h2 className="text-xl font-semibold mt-8 mb-4 text-gray-800 dark:text-white flex items-center">
                  <SafeIcon icon="BookOpen" className="mr-2 text-purple-500 dark:text-purple-400" size={20} useFa={true} />
                  {t('about.developerStory')}
                </h2>
                
                <p className="text-gray-600 dark:text-gray-300">
                  {t('about.story')}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
            <div className="p-8">
              <h2 className="text-2xl font-semibold mb-6 text-gray-800 dark:text-white flex items-center">
                <SafeIcon icon="Grid" className="mr-2 text-purple-500 dark:text-purple-400" size={24} useFa={true} />
                {t('about.otherTools')}
              </h2>

              <p className="text-gray-600 dark:text-gray-300 mb-8">
                {t('about.otherToolsDescription')}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {toolsList.map((tool, index) => (
                  <a key={index} href={tool.url} target="_blank" rel="noopener noreferrer" className="no-underline">
                    <Card className={`${tool.color} hover:shadow-lg transition-shadow duration-300 h-full border-0`}>
                      <div className="flex flex-col h-full">
                        <div className="flex items-center mb-3">
                          <div className={`w-10 h-10 rounded-full ${tool.iconColor.replace('text', 'bg').replace('500', '100')} dark:bg-opacity-30 flex items-center justify-center mr-3`}>
                            <SafeIcon icon={tool.icon} className={tool.iconColor} size={20} useFa={true} />
                          </div>
                          <h3 className="text-lg font-medium text-gray-800 dark:text-white m-0">{t(`about.tools.${tool.key}.name`)}</h3>
                        </div>
                        <p className="text-gray-600 dark:text-gray-300 text-sm flex-grow">{t(`about.tools.${tool.key}.description`)}</p>
                        <div className="mt-4 text-right">
                          <span className="text-purple-500 dark:text-purple-400 text-sm flex items-center justify-end">
                            {t('about.visitTool')}
                            <SafeIcon icon="ArrowRight" size={14} className="ml-1" useFa={true} />
                          </span>
                        </div>
                      </div>
                    </Card>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* 页脚区域 */}
      <footer className="bg-gray-800 text-white py-8 mt-12">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <Link to="/" className="flex items-center text-xl font-bold text-white">
                <img src="/favicon.svg" alt={t('about.logoText')} width="24" height="24" className="mr-2" />
                {t('about.logoText')}
              </Link>
            </div>
            
            <div className="flex space-x-8">
              <Link to="/privacy" className="text-gray-300 hover:text-white transition-colors text-sm">{tCommon('privacy')}</Link>
              <Link to="/terms" className="text-gray-300 hover:text-white transition-colors text-sm">{tCommon('terms')}</Link>
              <Link to="/copyright" className="text-gray-300 hover:text-white transition-colors text-sm">{tCommon('copyright')}</Link>
            </div>
            
            <div className="mt-4 md:mt-0 text-gray-400 text-sm">
              © {new Date().getFullYear()} {t('about.logoText')} | SnapBeautify
            </div>
          </div>
        </div>
      </footer>
      
      <BackTop>
        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-purple-600 hover:bg-purple-700 text-white shadow-lg">
          <SafeIcon icon="ChevronUp" size={20} useFa={true} />
        </div>
      </BackTop>
    </div>
  );
};

export default About; 